// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the rounds a meeting game plays, built on the presenter's page
 * (`docs/AYNI_XR_MEETING_GAMES_PLAN.md` §4–5), and the scoring of a data
 * round there, where the frame is. The room only referees (`gameReferee.ts`).
 *
 *   - **Trivia** (§4.1): any Khipu with quiz steps. Each quiz step is a
 *     round: the Khipu player puts the step on the globe, the quiz becomes
 *     a choice round with its right answer.
 *   - **Maypi** (§5): place rounds from AYNI 3D's 120 hand-checked places
 *     (a place's give-away line is the clue, its postcard the fact at the
 *     reveal), and, when the globe shows a value-encoded dataset, data
 *     rounds ("where is it highest right now?") scored by the percentile
 *     the pin's value reaches in the frame, as AYNI 3D's Maypi does.
 *
 * Pure but for the data probe, which is a parameter.
 */

import { WAYRA_PLACES } from '../ayni3d/shared/wayra-game/places'
import type { LocalizedString } from '../ayni3d/shared/dsa-types'
import type { Khipu } from './khipu'
import { planStep, type StepQuiz } from './khipuPlayback'
import { ACTIVITY_OPTION_CHARS, ACTIVITY_PROMPT_CHARS } from './roomActivity'
import { GAME_MAX_OPTIONS, GAME_MIN_OPTIONS, isDifficulty, percentileAccuracy, type RoundSpec } from './roomGame'

/** A trivia round and the Khipu step it stands on. */
export interface TriviaRound {
  /** The step's index in the Khipu's path. */
  step: number
  spec: RoundSpec
}

/** A quiz as a choice round, or null when it cannot be one. */
export function quizRound(quiz: StepQuiz, durationMs: number): RoundSpec | null {
  const options = quiz.options.map((o) => o.trim().slice(0, ACTIVITY_OPTION_CHARS)).filter(Boolean)
  if (options.length !== quiz.options.length || options.length < GAME_MIN_OPTIONS || options.length > GAME_MAX_OPTIONS) return null
  if (!Number.isInteger(quiz.correctIndex) || quiz.correctIndex < 0 || quiz.correctIndex >= options.length) return null
  const prompt = quiz.question.trim().slice(0, ACTIVITY_PROMPT_CHARS)
  if (!prompt) return null
  return { kind: 'choice', prompt, options, correct: quiz.correctIndex, difficulty: isDifficulty(quiz.difficulty) ? quiz.difficulty : 'medium', durationMs }
}

/** The trivia rounds of a Khipu: one per step with a quiz, in order. Empty when it has none. */
export function triviaRounds(k: Khipu, durationMs: number, lang: string): TriviaRound[] {
  if (!k.path?.steps?.length) return []
  const out: TriviaRound[] = []
  k.path.steps.forEach((step, i) => {
    const quiz = planStep(k, step, lang).quiz
    const spec = quiz ? quizRound(quiz, durationMs) : null
    if (spec) out.push({ step: i, spec })
  })
  return out
}

const text = (s: LocalizedString | undefined, lang: string): string => {
  if (!s) return ''
  const r = s as Record<string, string | undefined>
  return r[lang] ?? r[lang.slice(0, 2)] ?? r.en ?? ''
}

/** Maypi's forgiveness for a place round: full marks within this (km), then this scale (km). */
export const PLACE_RADIUS_KM = 150
export const PLACE_SCALE_KM = 2000

/**
 * `count` place rounds from the hand-checked places, never the same place
 * twice, in an order `random` decides. The clue is the place's give-away
 * line; the reveal names it and gives its postcard.
 */
export function placeRounds(count: number, durationMs: number, lang: string, words: { where: string }, random: () => number = Math.random): RoundSpec[] {
  const places = [...WAYRA_PLACES]
  for (let i = places.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[places[i], places[j]] = [places[j], places[i]]
  }
  const out: RoundSpec[] = []
  for (const p of places) {
    if (out.length >= count) break
    const clue = text(p.giveaway, lang)
    if (!clue) continue
    const prompt = `${clue} ${words.where}`.slice(0, ACTIVITY_PROMPT_CHARS)
    const label = `${text(p.name, lang)}, ${text(p.country, lang)}`.slice(0, ACTIVITY_OPTION_CHARS)
    out.push({ kind: 'point', prompt, answer: [p.lat, p.lon], label, fact: text(p.postcard, lang).slice(0, ACTIVITY_PROMPT_CHARS), radiusKm: PLACE_RADIUS_KM, scaleKm: PLACE_SCALE_KM, durationMs })
  }
  return out
}

/** A data round: no single right place; the presenter's page scores the pins from the frame. */
export function dataRound(prompt: string, durationMs: number): RoundSpec {
  return { kind: 'point', prompt: prompt.slice(0, ACTIVITY_PROMPT_CHARS), answer: null, label: '', fact: '', radiusKm: 0, scaleKm: 2000, durationMs }
}

/** A value at a place on the globe, or null for no data there. */
export type ValueProbe = (lat: number, lon: number) => number | null

/** The frame's values on a grid, each weighted by its cell's area (cos latitude), sorted. */
export interface FieldSample {
  values: number[]
  /** Cumulative weights, same order, ending at the total. */
  cumulative: number[]
}

/** Read the frame on a `stepDeg` grid: enough to rank a pin (the top few percent of a field are a plateau anyway). */
export function sampleField(probe: ValueProbe, stepDeg = 5): FieldSample {
  const points: [number, number][] = []
  for (let lat = -90 + stepDeg / 2; lat < 90; lat += stepDeg) {
    const w = Math.cos((lat * Math.PI) / 180)
    for (let lon = -180 + stepDeg / 2; lon < 180; lon += stepDeg) {
      const v = probe(lat, lon)
      if (v !== null && Number.isFinite(v)) points.push([v, w])
    }
  }
  points.sort((a, b) => a[0] - b[0])
  const cumulative: number[] = []
  let sum = 0
  for (const [, w] of points) {
    sum += w
    cumulative.push(sum)
  }
  return { values: points.map((p) => p[0]), cumulative }
}

/** The share of the field (by area) at or below `value`: 0 to 1. */
export function percentileIn(field: FieldSample, value: number): number {
  const n = field.values.length
  if (n === 0) return 0
  const total = field.cumulative[n - 1]
  // The last index whose value is <= value.
  let lo = 0
  let hi = n - 1
  let at = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (field.values[mid] <= value) {
      at = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return at < 0 ? 0 : field.cumulative[at] / total
}

/** A finger is not a texel: the most extreme value within about `radiusDeg` of the pin (AYNI 3D's tap tolerance). */
export function bestNear(probe: ValueProbe, lat: number, lon: number, high: boolean, radiusDeg = 2): number | null {
  let best: number | null = null
  for (let dy = -radiusDeg; dy <= radiusDeg; dy += radiusDeg / 2) {
    for (let dx = -radiusDeg; dx <= radiusDeg; dx += radiusDeg / 2) {
      const la = Math.max(-89.9, Math.min(89.9, lat + dy))
      const v = probe(la, lon + dx)
      if (v === null || !Number.isFinite(v)) continue
      if (best === null || (high ? v > best : v < best)) best = v
    }
  }
  return best
}

/** Each pin's accuracy on a data round: "highest" ranks by the share below, "lowest" by the share above. */
export function scoreDataPins(probe: ValueProbe, pins: readonly [string, number, number][], high: boolean, field: FieldSample = sampleField(probe)): [string, number][] {
  return pins.map(([pid, lat, lon]) => {
    const v = bestNear(probe, lat, lon, high)
    if (v === null) return [pid, 0]
    const below = percentileIn(field, v)
    // For "lowest", the share at or above the value: 1 − the share strictly below.
    const rank = high ? below : 1 - percentileIn(field, v - Number.EPSILON * Math.max(1, Math.abs(v)))
    return [pid, percentileAccuracy(rank)]
  })
}
